import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from 'components/ui/input-group';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { cn } from 'utils/cn';
import { formatShareAmount, formatShareRate } from './paymentShare';

/** Up to three whole digits, so a mistyped 120 can still be shown and refused. */
const RATE_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;
const AMOUNT_PATTERN = /^\d*(\.\d{0,2})?$/;

interface Props<T extends FieldValues> {
    control: Control<T>;
    name: FieldPath<T>;
    /** A percent with the "%" addon, or an amount. */
    kind: 'rate' | 'amount';
    ariaLabel: string;
    isInvalid?: boolean;
    /** The accepted text, with a comma typed as a dot, after the field has taken it. */
    onValueChange?: (value: string) => void;
    onBlur?: () => void;
    className?: string;
}

/**
 * A share's percent or amount input: the text as typed while it has focus, and the display format otherwise
 * ("60.00", "6 406.41"), so it reads like the totals beside it. Focusing it selects the whole value, so typing
 * replaces it: the text is right-aligned, and a click into its empty left part would otherwise put the caret before
 * the value, where most keystrokes make text the pattern refuses.
 */
export function ShareInput<T extends FieldValues>({
    control,
    name,
    kind,
    ariaLabel,
    isInvalid,
    onValueChange,
    onBlur,
    className,
}: Props<T>) {
    const { field } = useController({ control, name });
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    // The click that focuses the input places the caret when the button is released, after the focus has selected.
    const isFocusingClickRef = useRef(false);
    const pattern = kind === 'rate' ? RATE_PATTERN : AMOUNT_PATTERN;
    const format = kind === 'rate' ? formatShareRate : formatShareAmount;
    const raw = field.value === null || field.value === undefined ? '' : String(field.value);
    const { ref: fieldRef } = field;

    const setInputRef = useCallback(
        (element: HTMLInputElement | null) => {
            inputRef.current = element;
            fieldRef(element);
        },
        [fieldRef]
    );

    // Focus swaps the formatted text for the raw one, which moves the caret to the end; the selection follows it.
    useLayoutEffect(() => {
        if (isFocused) {
            inputRef.current?.select();
        }
    }, [isFocused]);

    return (
        <InputGroup className={cn('bg-paper ml-auto', className)}>
            <InputGroupInput
                ref={setInputRef}
                name={field.name}
                value={isFocused || raw === '' ? raw : format(raw)}
                inputMode="decimal"
                className="text-right tabular-nums"
                aria-label={ariaLabel}
                aria-invalid={isInvalid}
                onMouseDown={(event) => {
                    isFocusingClickRef.current = document.activeElement !== event.currentTarget;
                }}
                onMouseUp={(event) => {
                    if (isFocusingClickRef.current) {
                        isFocusingClickRef.current = false;
                        event.preventDefault();
                        event.currentTarget.select();
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setIsFocused(false);
                    field.onBlur();
                    onBlur?.();
                }}
                onChange={(event) => {
                    const next = event.target.value.replace(/,/g, '.');
                    if (pattern.test(next)) {
                        field.onChange(next);
                        onValueChange?.(next);
                    }
                }}
            />
            {kind === 'rate' ? (
                <InputGroupAddon align="inline-end">
                    <InputGroupText>%</InputGroupText>
                </InputGroupAddon>
            ) : null}
        </InputGroup>
    );
}

/** A payer's share of the net amount, to two decimals with the percent sign. */
export function ShareRateDisplay({ value }: { value: number }) {
    return <NumberFormat displayType="text" value={value} decimalScale={2} fixedDecimalScale suffix="%" />;
}
